/**
 * Shared error contract for every dashboard ↔ FastAPI request.
 *
 * Every non-2xx response body has the shape:
 *   { detail: { error_code: string | null, message: string | null } }
 *
 * UI code should:
 *   - read `error.message` to render text in the modal
 *   - key off `error.code` for special-case UI (buttons, layout, etc.)
 *
 * `error.code === null` means the failure was unexpected/internal — the
 * server logged the traceback but won't tell the browser anything more.
 * Render a generic "Action Failed" with no sub-line in that case.
 */

export class ActionError extends Error {
  constructor(
    public readonly code: string | null,
    public readonly userMessage: string | null,
    public readonly status: number,
  ) {
    super(userMessage ?? `Action failed (HTTP ${status})`);
    this.name = 'ActionError';
  }
}

export type ErrorDetail = { error_code: string | null; message: string | null };

export function parseDetail(raw: unknown): ErrorDetail {
  // Preferred contract: { error_code, message } envelope.
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && 'error_code' in raw && 'message' in raw) {
    const d = raw as Record<string, unknown>;
    return {
      error_code: typeof d.error_code === 'string' ? d.error_code : null,
      message: typeof d.message === 'string' ? d.message : null,
    };
  }
  // FastAPI's plain HTTPException(detail="...") yields a bare string — surface it as the
  // message so the user sees the real reason (e.g. "No connection to leader arms")
  // instead of a generic "Action failed (HTTP nnn)".
  if (typeof raw === 'string' && raw.trim()) {
    return { error_code: null, message: raw.trim() };
  }
  // FastAPI request-validation errors: detail is a list of { loc, msg, type }.
  if (Array.isArray(raw)) {
    const msgs = raw
      .map((e) =>
        e && typeof e === 'object' && typeof (e as Record<string, unknown>).msg === 'string'
          ? ((e as Record<string, unknown>).msg as string)
          : null,
      )
      .filter((m): m is string => !!m);
    if (msgs.length) return { error_code: null, message: msgs.join('; ') };
  }
  return { error_code: null, message: null };
}
